/**
 * Mapeamento entre as faixas exibidas na tela (De / Até) e os limiares
 * persistidos pela API (`MatrizRfmConfigInput` — um limiar por nota).
 *
 * IMPORTANTE: este arquivo NÃO calcula nota, score composto nem segmento.
 * Toda alocação vem de POST /api/v4/rfm/:idfornecedor/simular.
 */

export const RFM_CONFIG_PADRAO = Object.freeze({
  r5_dias: 15,
  r4_dias: 30,
  r3_dias: 60,
  r2_dias: 90,
  r1_dias: 365,
  f5_pedidos: 12,
  f4_pedidos: 9,
  f3_pedidos: 6,
  f2_pedidos: 3,
  f1_pedidos: 1,
  m5_rs: 50000,
  m4_rs: 30000,
  m3_rs: 15000,
  m2_rs: 5000,
  m1_rs: 0.01,
  ativa: true,
  periodo_analise: 12
});

export const PERIODOS_ANALISE = [3, 6, 12];

/** Insumos iniciais do simulador, restaurados junto com os valores padrão. */
export const SIMULADOR_INSUMOS_PADRAO = Object.freeze({
  diasDesdeUltimoPedido: 12,
  qtdPedidosJanela: 15,
  somaTotalliquidoJanela: 65000
});

export const CAMPOS_RECENCIA = [
  "r5_dias",
  "r4_dias",
  "r3_dias",
  "r2_dias",
  "r1_dias"
];
export const CAMPOS_FREQUENCIA = [
  "f5_pedidos",
  "f4_pedidos",
  "f3_pedidos",
  "f2_pedidos",
  "f1_pedidos"
];
export const CAMPOS_MONETARIO = ["m5_rs", "m4_rs", "m3_rs", "m2_rs", "m1_rs"];

const CAMPOS_NUMERICOS = [
  ...CAMPOS_RECENCIA,
  ...CAMPOS_FREQUENCIA,
  ...CAMPOS_MONETARIO
];

/** Menor valor aceito pela API para um limiar monetário (m1_rs não pode ser 0). */
export const MONETARIO_MINIMO = 0.01;
/** Passo usado para fechar a faixa monetária anterior (Até = próximo limiar - 0,01). */
export const MONETARIO_PASSO = 0.01;

export function arredondarMoeda(valor) {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return null;
  return Math.round(numero * 100) / 100;
}

function paraNumero(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
}

export function configPadrao() {
  return { ...RFM_CONFIG_PADRAO };
}

export function insumosPadrao() {
  return { ...SIMULADOR_INSUMOS_PADRAO };
}

/**
 * Reduz a resposta do GET (que também traz metadados de execução) ao shape
 * exato aceito pelo PUT, preenchendo com o padrão o que vier ausente.
 */
export function normalizarConfig(bruto) {
  const base = configPadrao();
  if (!bruto || typeof bruto !== "object") return base;

  const config = { ...base };
  CAMPOS_NUMERICOS.forEach(campo => {
    const numero = paraNumero(bruto[campo]);
    if (numero !== null) config[campo] = numero;
  });

  if (bruto.ativa !== undefined && bruto.ativa !== null) {
    config.ativa = bruto.ativa === true || bruto.ativa === "true";
  }
  const periodo = paraNumero(bruto.periodo_analise);
  if (periodo !== null) config.periodo_analise = periodo;

  return config;
}

/** Body do PUT / do campo `config` do simulador. */
export function configParaPayload(config) {
  const payload = {};
  CAMPOS_NUMERICOS.forEach(campo => {
    payload[campo] = config[campo];
  });
  payload.ativa = config.ativa === true;
  payload.periodo_analise = config.periodo_analise;
  return payload;
}

/* -------------------------------------------------------------------------
 * Recência — nota N quando `dias <= rN_dias` (menos dias, nota maior).
 * Coluna "De" é derivada do limiar da nota acima; "Até" é o próprio limiar.
 * ---------------------------------------------------------------------- */
export function faixasRecencia(config) {
  return [
    { score: 5, de: 0, ate: config.r5_dias, semLimite: false },
    {
      score: 4,
      de: proximoDia(config.r5_dias),
      ate: config.r4_dias,
      semLimite: false
    },
    {
      score: 3,
      de: proximoDia(config.r4_dias),
      ate: config.r3_dias,
      semLimite: false
    },
    {
      score: 2,
      de: proximoDia(config.r3_dias),
      ate: config.r2_dias,
      semLimite: false
    },
    { score: 1, de: proximoDia(config.r2_dias), ate: null, semLimite: true }
  ];
}

function proximoDia(limiar) {
  const numero = paraNumero(limiar);
  return numero === null ? null : numero + 1;
}

const CAMPO_RECENCIA_POR_SCORE = {
  5: "r5_dias",
  4: "r4_dias",
  3: "r3_dias",
  2: "r2_dias"
};

/**
 * Altera o "Até" da nota informada (5..2) e recascadeia as notas menores.
 * O "De" das demais notas é derivado, então basta reescrever o limiar.
 */
export function aplicarAteRecencia(config, score, valor) {
  const campo = CAMPO_RECENCIA_POR_SCORE[score];
  if (!campo) return { ...config };

  const numero = paraNumero(valor);
  const atualizado = { ...config, [campo]: numero };

  // r1_dias não é editável na UI ("Sem limite"), mas a API exige o campo e a
  // monotonicidade r2_dias <= r1_dias.
  const r2 = paraNumero(atualizado.r2_dias);
  const r1 = paraNumero(atualizado.r1_dias);
  if (r2 !== null && (r1 === null || r1 < r2)) {
    atualizado.r1_dias = r2;
  }

  return atualizado;
}

/* -------------------------------------------------------------------------
 * Frequência — nota N quando `pedidos >= fN_pedidos`.
 * Coluna "De" é o próprio limiar; "Até" fecha na nota acima menos 1 pedido.
 * ---------------------------------------------------------------------- */
export function faixasFrequencia(config) {
  return [
    { score: 5, de: config.f5_pedidos, ate: null, semLimite: true },
    {
      score: 4,
      de: config.f4_pedidos,
      ate: pedidoAnterior(config.f5_pedidos),
      semLimite: false
    },
    {
      score: 3,
      de: config.f3_pedidos,
      ate: pedidoAnterior(config.f4_pedidos),
      semLimite: false
    },
    {
      score: 2,
      de: config.f2_pedidos,
      ate: pedidoAnterior(config.f3_pedidos),
      semLimite: false
    },
    {
      score: 1,
      de: config.f1_pedidos,
      ate: pedidoAnterior(config.f2_pedidos),
      semLimite: false
    }
  ];
}

function pedidoAnterior(limiar) {
  const numero = paraNumero(limiar);
  return numero === null ? null : numero - 1;
}

const CAMPO_FREQUENCIA_ACIMA_POR_SCORE = {
  4: "f5_pedidos",
  3: "f4_pedidos",
  2: "f3_pedidos",
  1: "f2_pedidos"
};

/** O "Até" da nota N define o limiar da nota N+1 (`Até + 1`). */
export function aplicarAteFrequencia(config, score, valor) {
  const campo = CAMPO_FREQUENCIA_ACIMA_POR_SCORE[score];
  if (!campo) return { ...config };

  const numero = paraNumero(valor);
  return { ...config, [campo]: numero === null ? null : numero + 1 };
}

/* -------------------------------------------------------------------------
 * Monetário — nota N quando `valor >= mN_rs`.
 * Coluna "De" é o próprio limiar; "Até" fecha na nota acima menos R$ 0,01.
 * ---------------------------------------------------------------------- */
export function faixasMonetario(config) {
  return [
    { score: 5, de: config.m5_rs, ate: null, semLimite: true },
    {
      score: 4,
      de: config.m4_rs,
      ate: valorAnterior(config.m5_rs),
      semLimite: false
    },
    {
      score: 3,
      de: config.m3_rs,
      ate: valorAnterior(config.m4_rs),
      semLimite: false
    },
    {
      score: 2,
      de: config.m2_rs,
      ate: valorAnterior(config.m3_rs),
      semLimite: false
    },
    // A API rejeita m1_rs = 0; na tela o início da nota 1 é apresentado como 0.
    { score: 1, de: 0, ate: valorAnterior(config.m2_rs), semLimite: false }
  ];
}

function valorAnterior(limiar) {
  const numero = paraNumero(limiar);
  return numero === null ? null : arredondarMoeda(numero - MONETARIO_PASSO);
}

const CAMPO_MONETARIO_ACIMA_POR_SCORE = {
  4: "m5_rs",
  3: "m4_rs",
  2: "m3_rs",
  1: "m2_rs"
};

/** O "Até" da nota N define o limiar da nota N+1 (`Até + 0,01`). */
export function aplicarAteMonetario(config, score, valor) {
  const campo = CAMPO_MONETARIO_ACIMA_POR_SCORE[score];
  if (!campo) return { ...config };

  const numero = paraNumero(valor);
  return {
    ...config,
    [campo]: numero === null ? null : arredondarMoeda(numero + MONETARIO_PASSO)
  };
}

/* ------------------------------- Padrões -------------------------------- */
export function restaurarDimensao(config, dimensao) {
  const campos = {
    recencia: CAMPOS_RECENCIA,
    frequencia: CAMPOS_FREQUENCIA,
    monetario: CAMPOS_MONETARIO
  }[dimensao];

  if (!campos) return { ...config };

  const atualizado = { ...config };
  campos.forEach(campo => {
    atualizado[campo] = RFM_CONFIG_PADRAO[campo];
  });
  return atualizado;
}

/* ------------------------------ Validação ------------------------------- */
function erro(dimensao, score, mensagem) {
  return { dimensao, score, mensagem };
}

function validarLimiarPositivo(valor, dimensao, score, rotulo) {
  const numero = paraNumero(valor);
  if (numero === null) {
    return erro(
      dimensao,
      score,
      `Informe o valor de ${rotulo} da nota ${score}.`
    );
  }
  if (numero <= 0) {
    return erro(
      dimensao,
      score,
      `${rotulo} da nota ${score} deve ser um número maior que zero.`
    );
  }
  return null;
}

export function validarRecencia(config) {
  const erros = [];
  const faixas = faixasRecencia(config);

  faixas
    .filter(faixa => !faixa.semLimite)
    .forEach(faixa => {
      const invalido = validarLimiarPositivo(
        faixa.ate,
        "recencia",
        faixa.score,
        "Até (dias)"
      );
      if (invalido) {
        erros.push(invalido);
        return;
      }
      if (faixa.de !== null && faixa.ate < faixa.de) {
        erros.push(
          erro(
            "recencia",
            faixa.score,
            `A faixa da nota ${faixa.score} é inválida: o início (${
              faixa.de
            } dias) ultrapassa o fim (${
              faixa.ate
            } dias). Aumente o limite da nota ${
              faixa.score
            } ou reduza o da nota ${faixa.score + 1}.`
          )
        );
      }
    });

  return erros;
}

export function validarFrequencia(config) {
  const erros = [];
  const faixas = faixasFrequencia(config);

  faixas.forEach(faixa => {
    const invalido = validarLimiarPositivo(
      faixa.de,
      "frequencia",
      faixa.score,
      "De (pedidos)"
    );
    if (invalido) {
      erros.push(invalido);
      return;
    }
    if (!Number.isInteger(Number(faixa.de))) {
      erros.push(
        erro(
          "frequencia",
          faixa.score,
          `A nota ${faixa.score} aceita apenas quantidades inteiras de pedidos.`
        )
      );
    }
    if (!faixa.semLimite && faixa.ate !== null && faixa.ate < faixa.de) {
      erros.push(
        erro(
          "frequencia",
          faixa.score,
          `A faixa da nota ${faixa.score} é inválida: o início (${
            faixa.de
          } pedidos) ultrapassa o fim (${
            faixa.ate
          } pedidos). Ajuste o limite da nota ${
            faixa.score
          } ou da nota ${faixa.score + 1}.`
        )
      );
    }
  });

  return erros;
}

export function validarMonetario(config) {
  const erros = [];
  const faixas = faixasMonetario(config);
  const limiares = {
    5: config.m5_rs,
    4: config.m4_rs,
    3: config.m3_rs,
    2: config.m2_rs,
    1: config.m1_rs
  };

  faixas.forEach(faixa => {
    const invalido = validarLimiarPositivo(
      limiares[faixa.score],
      "monetario",
      faixa.score,
      "De (R$)"
    );
    if (invalido) {
      erros.push(invalido);
      return;
    }
    const de = paraNumero(limiares[faixa.score]);
    if (!faixa.semLimite && faixa.ate !== null && faixa.ate < de) {
      erros.push(
        erro(
          "monetario",
          faixa.score,
          `A faixa da nota ${
            faixa.score
          } é inválida: o início ultrapassa o fim. Ajuste o limite da nota ${
            faixa.score
          } ou da nota ${faixa.score + 1}.`
        )
      );
    }
  });

  return erros;
}

export function validarPeriodo(config) {
  const periodo = paraNumero(config.periodo_analise);
  if (periodo === null || !Number.isInteger(periodo) || periodo < 1) {
    return [
      erro(
        "geral",
        null,
        "O período de análise deve ser um inteiro maior ou igual a 1."
      )
    ];
  }
  return [];
}

/** Todos os erros de faixa da tela. Enquanto houver algum, o PUT é bloqueado. */
export function validarConfig(config) {
  return [
    ...validarPeriodo(config),
    ...validarRecencia(config),
    ...validarFrequencia(config),
    ...validarMonetario(config)
  ];
}

/* ------------------------------ Formatação ------------------------------ */
const formatadorMoeda = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

export function formatarMoeda(valor) {
  const numero = paraNumero(valor);
  if (numero === null) return "-";
  return formatadorMoeda.format(numero);
}
